/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import {
  Search,
  Filter,
  Plus,
  MoreHorizontal,
  Eye,
  FileText,
  File,
  ChevronDown,
  Calendar,
  X,
  Download,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Pagination from "../../../components/Pagination";

// --- Mock Data ---

const MOCK_TEMPLATE_CONTENT = `AGREEMENT FOR CAREGIVER SERVICES

This Agreement is made on {{date}} between {{provider_name}} (Service Provider) and {{buyer_name}} (Service Buyer).

1. SERVICES PROVIDED
The Caregiver agrees to provide home care services, including but not limited to assistance with daily living activities, meal preparation, and companionship.

2. COMPENSATION
Payment of agreed amount shall be made on a weekly basis.

3. CONFIDENTIALITY
The Caregiver agrees to keep all client information confidential.

4. TERM AND TERMINATION
This agreement shall commence on {{date}} and continue until terminated by either party with appropriate notice.`;

const generateTemplates = () => [
  {
    id: "TMP-1",
    name: "Standard Caregiver Contract",
    type: "Automatic",
    trigger: "Caregiver Assignment",
    recipients: "Caregiver, Client",
    createdDate: "Oct 10, 2024",
    status: "Active",
  },
  {
    id: "TMP-2",
    name: "Emergency Medical Authorization",
    type: "Manual",
    trigger: "Manual Send",
    recipients: "Client",
    createdDate: "Oct 12, 2024",
    status: "Active",
  },
  {
    id: "TMP-3",
    name: "Non-Disclosure Agreement",
    type: "Automatic",
    trigger: "Onboarding Complete",
    recipients: "Caregiver",
    createdDate: "Nov 01, 2024",
    status: "Inactive",
  },
  {
    id: "TMP-4",
    name: "Service Termination Policy",
    type: "Manual",
    trigger: "Manual Send",
    recipients: "Client",
    createdDate: "Nov 05, 2024",
    status: "Draft",
  },
  {
    id: "TMP-5",
    name: "Live-in Care Addendum",
    type: "Automatic",
    trigger: "Job Post (Live-in)",
    recipients: "Caregiver, Client",
    createdDate: "Nov 15, 2024",
    status: "Active",
  },
];

const generateCompleted = () => {
  return Array.from({ length: 15 }, (_, i) => ({
    id: `AGR-${1000 + i}`,
    agreementNumber: `#AGR-${202400 + i}`,
    templateName:
      i % 2 === 0 ? "Standard Caregiver Contract" : "Emergency Medical Auth",
    provider: ["Bethelhem Alemu", "Tigist Bekele", "Hana Girma"][i % 3],
    buyer: ["Yonatan Solomon", "Sarah Smith", "Michael Chen"][i % 3],
    dateSigned: new Date(Date.now() - i * 86400000 * 2).toLocaleDateString(
      "en-US",
      { month: "short", day: "numeric", year: "numeric" },
    ),
    status: i % 5 === 0 ? "Pending" : i % 10 === 0 ? "Expired" : "Signed",
  }));
};

const Agreements = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("templates");

  // Data
  const [templates] = useState(generateTemplates());
  const [completedList] = useState(generateCompleted());

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [timeFilter, setTimeFilter] = useState("All"); // For completed tab

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Active Menu State
  const [activeMenu, setActiveMenu] = useState(null);

  // Preview State
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState({
    title: "",
    content: "",
    isSigned: false,
  });

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, timeFilter, activeTab]);

  // Close menus on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest(".action-menu")) return;
      setActiveMenu(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // --- Filtering Logic ---

  const filteredTemplates = templates.filter((t) => {
    const matchesSearch = t.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "All Status" || t.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredCompleted = completedList.filter((c) => {
    const matchesSearch =
      c.agreementNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.templateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.buyer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "All Status" || c.status === statusFilter;
    // Note: Time filter implementation would go here (simplified for mock)
    return matchesSearch && matchesStatus;
  });

  const currentData =
    activeTab === "templates" ? filteredTemplates : filteredCompleted;
  const totalPages = Math.ceil(currentData.length / itemsPerPage);
  const paginatedData = currentData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // --- Handlers ---

  const openPreview = (agreement) => {
    // Inject dynamic data into mock template
    const filledContent = MOCK_TEMPLATE_CONTENT.replace(
      "{{date}}",
      agreement.dateSigned,
    )
      .replace("{{date}}", agreement.dateSigned)
      .replace("{{provider_name}}", agreement.provider)
      .replace("{{buyer_name}}", agreement.buyer);

    setPreviewContent({
      title: `${agreement.templateName} - ${agreement.agreementNumber}`,
      content: filledContent,
      isSigned: agreement.status === "Signed",
    });

    setActiveMenu(null);
    setShowPreview(true);
  };

  // --- Helper Components ---

  const StatusBadge = ({ status }) => {
    const styles = {
      Active:
        "bg-primary/10 text-primary border-primary/20 dark:bg-primary/20 dark:text-primary dark:border-primary/30",
      Signed:
        "bg-primary/10 text-primary border-primary/20 dark:bg-primary/20 dark:text-primary dark:border-primary/30",
      Inactive:
        "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800",
      Expired:
        "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800",
      Draft:
        "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700 dark:text-dark-muted dark:border-gray-600",
      Pending:
        "bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800",
    };
    return (
      <span
        className={`px-2.5 py-1 rounded-full text-xs font-bold border ${
          styles[status] || styles["Draft"]
        }`}
      >
        {status}
      </span>
    );
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12 bg-background dark:bg-dark-background min-h-screen p-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-text dark:text-dark-text">
            Agreements
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1">
            Manage templates and track signed contracts.
          </p>
        </div>
        {activeTab === "templates" && (
          <button
            onClick={() => navigate("/agreements/create")}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold shadow-lg shadow-primary/20 dark:shadow-none transition-all"
          >
            <Plus size={18} /> Create Agreement Template
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-6 mb-6 border-b border-gray-200 dark:border-dark-border">
        <button
          onClick={() => {
            setActiveTab("templates");
            setSearchTerm("");
            setStatusFilter("All Status");
          }}
          className={`pb-3 text-sm font-bold transition-all relative ${
            activeTab === "templates"
              ? "text-primary dark:text-primary"
              : "text-gray-500 dark:text-dark-muted hover:text-gray-700 dark:hover:text-dark-text"
          }`}
        >
          Agreement Templates
          {activeTab === "templates" && (
            <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary dark:bg-primary rounded-t-full"></span>
          )}
        </button>
        <button
          onClick={() => {
            setActiveTab("completed");
            setSearchTerm("");
            setStatusFilter("All Status");
          }}
          className={`pb-3 text-sm font-bold transition-all relative ${
            activeTab === "completed"
              ? "text-primary dark:text-primary"
              : "text-gray-500 dark:text-dark-muted hover:text-gray-700 dark:hover:text-dark-text"
          }`}
        >
          Completed Agreements
          {activeTab === "completed" && (
            <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary dark:bg-primary rounded-t-full"></span>
          )}
        </button>
      </div>

      {/* Filters Toolbar */}
      <div className="bg-card dark:bg-dark-surface p-4 rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border mb-6 flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-muted"
            size={18}
          />
          <input
            type="text"
            placeholder={
              activeTab === "templates"
                ? "Search templates by name..."
                : "Search by agreement #, template, or client..."
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-11 pr-4 py-2.5 rounded-xl bg-accent dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 dark:placeholder-dark-muted text-sm font-medium transition-all"
          />
        </div>

        <div className="flex gap-3">
          {activeTab === "completed" && (
            <div className="relative min-w-[140px]">
              <select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
                className="w-full appearance-none pl-4 pr-10 py-2.5 rounded-xl bg-accent dark:bg-gray-900 border-none outline-none text-text dark:text-dark-text text-sm font-medium cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <option>All Time</option>
                <option>Today</option>
                <option>This Week</option>
                <option>Last Week</option>
                <option>This Month</option>
              </select>
              <Calendar
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-muted pointer-events-none"
                size={14}
              />
            </div>
          )}

          <div className="relative min-w-[140px]">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full appearance-none pl-4 pr-10 py-2.5 rounded-xl bg-accent dark:bg-gray-900 border-none outline-none text-text dark:text-dark-text text-sm font-medium cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <option>All Status</option>
              {activeTab === "templates" ? (
                <>
                  <option>Active</option>
                  <option>Inactive</option>
                  <option>Draft</option>
                </>
              ) : (
                <>
                  <option>Signed</option>
                  <option>Pending</option>
                  <option>Expired</option>
                </>
              )}
            </select>
            <ChevronDown
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-muted pointer-events-none"
              size={14}
            />
          </div>

          <button className="px-4 py-2.5 bg-accent dark:bg-gray-900 text-gray-600 dark:text-dark-muted rounded-xl text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors border-none flex items-center gap-2">
            <Filter size={16} /> Filter
          </button>
        </div>
      </div>

      {/* Tables Content */}
      <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden min-h-[400px]">
        {paginatedData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-accent dark:bg-gray-900/50 text-xs text-gray-500 dark:text-dark-muted uppercase font-semibold border-b border-gray-100 dark:border-dark-border">
                <tr>
                  {activeTab === "templates" ? (
                    <>
                      <th className="px-6 py-4">Template Name</th>
                      <th className="px-6 py-4">Type</th>
                      <th className="px-6 py-4">Trigger</th>
                      <th className="px-6 py-4">Recipients</th>
                      <th className="px-6 py-4">Created Date</th>
                      <th className="px-6 py-4 text-center">Status</th>
                      <th className="px-6 py-4 text-center">Actions</th>
                    </>
                  ) : (
                    <>
                      <th className="px-6 py-4">Agreement #</th>
                      <th className="px-6 py-4">Template</th>
                      <th className="px-6 py-4">Service Provider</th>
                      <th className="px-6 py-4">Service Buyer</th>
                      <th className="px-6 py-4">Date Signed</th>
                      <th className="px-6 py-4 text-center">Status</th>
                      <th className="px-6 py-4 text-center">Actions</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {activeTab === "templates"
                  ? paginatedData.map((t) => (
                      <tr
                        key={t.id}
                        className="hover:bg-accent dark:hover:bg-gray-800/50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary/10 dark:bg-primary/20 text-primary rounded-lg">
                              <FileText size={18} />
                            </div>
                            <span className="font-bold text-text dark:text-dark-text">
                              {t.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-dark-muted">
                          {t.type}
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-dark-muted">
                          {t.trigger}
                        </td>
                        <td className="px-6 py-4">
                          <span className="bg-accent dark:bg-gray-700 px-2 py-1 rounded text-xs font-medium text-gray-600 dark:text-dark-muted">
                            {t.recipients}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-500 dark:text-dark-muted">
                          {t.createdDate}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <StatusBadge status={t.status} />
                        </td>
                        <td className="px-6 py-4 text-center relative action-menu">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveMenu(activeMenu === t.id ? null : t.id);
                            }}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-accent dark:hover:bg-gray-700 rounded-lg transition-colors"
                          >
                            <MoreHorizontal size={18} />
                          </button>
                          {activeMenu === t.id && (
                            <div className="absolute right-8 top-8 w-40 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top-right">
                              <button
                                onClick={() =>
                                  navigate(`/agreements/templates/${t.id}`)
                                }
                                className="w-full text-left px-4 py-2.5 text-sm text-text dark:text-dark-text hover:bg-accent dark:hover:bg-gray-700 flex items-center gap-2"
                              >
                                <Eye size={16} /> View Details
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  : paginatedData.map((c) => (
                      <tr
                        key={c.id}
                        className="hover:bg-accent dark:hover:bg-gray-800/50 transition-colors"
                      >
                        <td className="px-6 py-4 font-mono text-xs text-gray-500 dark:text-dark-muted">
                          {c.agreementNumber}
                        </td>
                        <td className="px-6 py-4 font-medium text-text dark:text-dark-text">
                          {c.templateName}
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-dark-muted">
                          {c.provider}
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-dark-muted">
                          {c.buyer}
                        </td>
                        <td className="px-6 py-4 text-gray-500 dark:text-dark-muted">
                          {c.dateSigned}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <StatusBadge status={c.status} />
                        </td>
                        <td className="px-6 py-4 text-center relative action-menu">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveMenu(activeMenu === c.id ? null : c.id);
                            }}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-accent dark:hover:bg-gray-700 rounded-lg transition-colors"
                          >
                            <MoreHorizontal size={18} />
                          </button>
                          {activeMenu === c.id && (
                            <div className="absolute right-8 top-8 w-40 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top-right">
                              <button
                                onClick={() => openPreview(c)}
                                className="w-full text-left px-4 py-2.5 text-sm text-text dark:text-dark-text hover:bg-accent dark:hover:bg-gray-700 flex items-center gap-2"
                              >
                                <Eye size={16} /> View Details
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 bg-accent dark:bg-gray-800 rounded-full flex items-center justify-center mb-4 text-gray-400 dark:text-dark-muted">
              <File size={32} />
            </div>
            <h3 className="text-lg font-bold text-text dark:text-dark-text mb-1">
              No Agreements Found
            </h3>
            <p className="text-gray-500 dark:text-dark-muted max-w-sm">
              {activeTab === "templates"
                ? "You haven't created any agreement templates yet. Click the button above to start."
                : "No completed agreements found matching your current filters."}
            </p>
          </div>
        )}

        {/* Pagination */}
        <div className="border-t border-gray-100 dark:border-dark-border bg-accent/50 dark:bg-gray-800/30 p-2">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-3xl h-[85vh] flex flex-col animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-gray-100 dark:border-dark-border flex justify-between items-center bg-accent/50 dark:bg-gray-800/50 rounded-t-2xl">
              <h3 className="text-lg font-bold text-text dark:text-dark-text flex items-center gap-2">
                <FileText size={20} className="text-primary" />{" "}
                {previewContent.isSigned
                  ? "Signed Contract"
                  : "Agreement Preview"}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  className="p-2 hover:bg-accent dark:hover:bg-gray-700 rounded-full text-gray-500 dark:text-dark-muted transition-colors"
                  title="Download PDF"
                >
                  <Download size={20} />
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 hover:bg-accent dark:hover:bg-gray-700 rounded-full text-gray-500 dark:text-dark-muted transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-8 md:p-12 bg-accent dark:bg-black/20 custom-scrollbar">
              <div className="bg-card dark:bg-dark-surface p-8 md:p-12 shadow-sm border border-gray-200 dark:border-dark-border min-h-full mx-auto max-w-2xl relative">
                {previewContent.isSigned && (
                  <div className="absolute top-10 right-10 border-4 border-primary text-primary rounded-full w-24 h-24 flex items-center justify-center opacity-30 -rotate-12 pointer-events-none">
                    <span className="font-black text-xl uppercase">SIGNED</span>
                  </div>
                )}

                <h1 className="text-2xl font-bold text-center mb-8 uppercase tracking-widest border-b-2 border-black dark:border-white pb-4">
                  {previewContent.title}
                </h1>

                <div className="whitespace-pre-wrap font-serif leading-loose text-text dark:text-dark-muted text-sm">
                  {previewContent.content}
                </div>

                <div className="mt-16 grid grid-cols-2 gap-12 pt-8">
                  <div>
                    <div className="h-px bg-black dark:bg-gray-500 mb-2"></div>
                    <p className="text-xs font-bold uppercase">
                      Service Provider Signature
                    </p>
                    {previewContent.isSigned && (
                      <p className="font-script text-lg text-primary mt-[-2.5rem] ml-4 -rotate-3">
                        Signed Digitally
                      </p>
                    )}
                  </div>
                  <div>
                    <div className="h-px bg-black dark:bg-gray-500 mb-2"></div>
                    <p className="text-xs font-bold uppercase">
                      Service Buyer Signature
                    </p>
                    {previewContent.isSigned && (
                      <p className="font-script text-lg text-primary mt-[-2.5rem] ml-4 -rotate-3">
                        Signed Digitally
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Agreements;
